import { CatalogItem } from "interfaces";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";

/* Prefix used for taxonomy identifiers */
export const TAXONOMY_PREFIX = "atn";

/**
 * Capitalizes the first letter of a string and converts the rest to lowercase
 *
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Parses a taxonomy ID string into a TaxonomyIdItem object
 *
 * @param taxonomyId - The taxonomy ID string to parse
 * @returns A TaxonomyIdItem containing parsed taxonomy data
 */
export const parseTaxonomy = (taxonomyId: string): TaxonomyIdItem => {
    return new TaxonomyIdItem(taxonomyId);
};

/**
 * Builds a taxonomy path string from data domain and item levels up to a specific index
 * This name uses a temporal name, as it's a requirement of the taxonomy lib and then removes it
 * To retrieve the formatted taxonomyId
 *
 * @param dataDomain - The data domain for the taxonomy
 * @param itemLevels - Array of taxonomy level names
 * @param amountOfLevels - The index up to which to include levels (inclusive)
 * @returns A formatted taxonomy path string
 */
export const buildTaxonomyPath = (dataDomain: string, itemLevels: string[], amountOfLevels: number): string => {
    const temporalName: string = "name";
    const taxonomyId: TaxonomyIdItem = TaxonomyIdItem.buildFrom(
        dataDomain,
        itemLevels.slice(0, amountOfLevels + 1),
        temporalName,
    );

    return taxonomyId.toString().replace(`/${temporalName}`, "");
};

/**
 * Gets an existing filter from the map or creates a new one if it doesn't exist
 *
 * @param taxonomyId - The unique taxonomy identifier
 * @param name - The display name for the filter
 * @param filterMap - Map containing existing filters
 * @returns The existing or newly created CheckboxNode filter
 */
export const getOrCreateFilter = (
    taxonomyId: string,
    name: string,
    filterMap: Map<string, CheckboxNode>,
): CheckboxNode => {
    if (!filterMap.has(taxonomyId)) {
        filterMap.set(taxonomyId, {
            id: taxonomyId,
            name: capitalizeFirstLetter(name),
            children: [],
        });
    }
    return filterMap.get(taxonomyId)!;
};

/**
 * Converts an array of CheckboxNode children to a Map indexed by id
 *
 * @param children - Array of CheckboxNode objects
 * @returns Map with id as key and CheckboxNode as value
 */
export const childrenToMap = (children: CheckboxNode[]): Map<string, CheckboxNode> => {
    const map = new Map<string, CheckboxNode>();
    children.forEach((child) => map.set(child.id, child));
    return map;
};

/**
 * Converts catalog data into a hierarchical filter structure
 * Processes taxonomy IDs from catalog items and creates the CheckboxNode filters
 *
 * @param catalog - Array of catalog request response items containing taxonomy data
 * @param currentFilters - The current filters in the application. This is used to preserve filters.
 * @returns Array of root-level CheckboxNode filters with nested children
 */
export const retrieveFiltersFromCatalog = (
    catalog: CatalogItem[],
    currentFilters: CheckboxNode[] = [],
): CheckboxNode[] => {
    const rootFilters = childrenToMap(currentFilters);

    catalog.forEach((item) => {
        const { itemLevels, dataDomain } = parseTaxonomy(item.taxonomyId);

        let parentFilter: CheckboxNode | null = null;

        itemLevels.forEach((level, index) => {
            const taxonomyPath = buildTaxonomyPath(dataDomain, itemLevels, index);
            const targetMap = index === 0 ? rootFilters : childrenToMap(parentFilter!.children!);

            const filter = getOrCreateFilter(taxonomyPath, level, targetMap);

            if (parentFilter?.children) {
                if (!parentFilter.children.find((child) => child.id === filter.id)) {
                    parentFilter.children.push(filter);
                }
            }

            parentFilter = filter;
        });
    });

    return Array.from(rootFilters.values());
};
